import {coordsToKey} from '../../coords/coordsToKey';
import {HEX_DIRECTIONS} from '../../../constants/geometry';
import {SUPPLY_UNPASSABLE_TOKENS} from '../../../configs/supply';
import {MAX_SUPPLY_DISTANCE} from '../../../constants/supply';
import {COMMON_BUILDING_TOKENS, TOKENS} from '../../../constants/tokens';
import {checkIfHexBlockedByEnemyUnit} from '../checkIfHexBlockedByEnemyUnit';

/**
 * Поиск ПУТЕЙ снабжения для анимаций (calculateAvailableSupplyCells отвечает
 * только «достижим ли отряд», но самих путей не возвращает). Правила прохода
 * повторяют канонический алгоритм:
 *  - путь идет только по НЕПУСТЫМ ячейкам без гор/воды и вражеских отрядов;
 *  - ячейка, смежная вражескому отряду, не продолжает путь (конечной точкой
 *    быть может);
 *  - конечная ячейка — любая (в том числе пустая или смежная врагу: отряд на
 *    ней снабдить можно, транзитом пройти нельзя);
 *  - дальность сегмента — MAX_SUPPLY_DISTANCE (5) ячеек, НЕ считая ячейки
 *    самой фермы; НЕзаблокированная ферма игрока на пути перезапускает
 *    дальность (цепочка ферм).
 */

const keyToQR = (hexKey) => hexKey.split(',').map(Number);

/**
 * Общий контекст обеих функций поиска: множества вражеских отрядов, ячеек
 * рядом с ними и ферм игрока. fieldHexes — ячейки ПОЛЯ (hexGridRenderer.hexes:
 * границы карты), hexes — состояние жетонов.
 */
export const buildSupplyPathContext = ({
                                         fieldHexes,
                                         hexes,
                                         units,
                                         currentPlayer,
                                         currentPlayerCities,
                                         specialBuildings,
                                         buildingsDamage,
                                       }) => {
  // Отряд, нанятый крысолюдами за снабжение (isSupplyRecruit), появится на поле
  // только по итогу фазы снабжения и до тех пор путей не перекрывает — как в
  // каноническом calculateAvailableSupplyCells и в checkIfHexBlockedByEnemyUnit.
  // Иначе путь для анимации рвался бы там, где снабжение на самом деле проходит
  const enemyUnitHexKeys = new Set(
    units
      .filter((unit) => unit.ownerId !== currentPlayer.id && !unit.isSupplyRecruit)
      .map((unit) => unit.hexKey)
  );

  const adjacentToEnemy = new Set();
  enemyUnitHexKeys.forEach((enemyKey) => {
    const [q, r] = keyToQR(enemyKey);
    for (const [dq, dr] of HEX_DIRECTIONS) {
      adjacentToEnemy.add(coordsToKey(q + dq, r + dr));
    }
  });

  const playerFarmKeysSet = new Set();
  (currentPlayerCities || []).forEach((city) => {
    (city[COMMON_BUILDING_TOKENS.FARM] || []).forEach((farmKey) => playerFarmKeysSet.add(farmKey));
  });

  return {
    fieldHexes,
    hexes,
    units,
    currentPlayer,
    specialBuildings,
    buildingsDamage,
    enemyUnitHexKeys,
    adjacentToEnemy,
    playerFarmKeysSet,
  };
};

// Рабочая (незаблокированная) ферма игрока — перезапускает дальность цепочки
// и годится финишем обратного поиска
const isWorkingPlayerFarm = (hexKey, ctx) => {
  if (!ctx.playerFarmKeysSet.has(hexKey)) return false;
  if (ctx.hexes[hexKey] !== COMMON_BUILDING_TOKENS.FARM) return false;
  return !checkIfHexBlockedByEnemyUnit(
    hexKey, TOKENS.FARM, ctx.currentPlayer.id, ctx.units, ctx.hexes, ctx.specialBuildings, ctx.buildingsDamage
  );
};

// Транзитная проходимость ПРОМЕЖУТОЧНОЙ ячейки пути (не старт и не финиш)
// по строгим правилам снабжения
const isTransitable = (hexKey, ctx) => {
  if (!ctx.fieldHexes[hexKey]) return false;
  const token = ctx.hexes[hexKey];
  // Пустая ячейка не продолжает путь (даже с отрядом игрока — тот может быть
  // только конечной точкой)
  if (!token) return false;
  if (SUPPLY_UNPASSABLE_TOKENS.includes(token)) return false;
  if (ctx.enemyUnitHexKeys.has(hexKey)) return false;
  // Смежная вражескому отряду ячейка обрывает путь
  if (ctx.adjacentToEnemy.has(hexKey)) return false;
  return true;
};

const neighborsOf = (hexKey) => {
  const [q, r] = keyToQR(hexKey);
  return HEX_DIRECTIONS.map(([dq, dr]) => coordsToKey(q + dq, r + dr));
};

const restorePath = (parents, endStateKey) => {
  const path = [];
  let cursor = endStateKey;
  while (cursor) {
    path.push(cursor.split('@')[0]);
    cursor = parents.get(cursor);
  }
  return path.reverse();
};

/**
 * Кратчайший путь снабжения от КОНКРЕТНОЙ фермы к ячейке (анимация раздачи в
 * фазе снабжения). Состояние поиска — (ячейка, остаток дальности): вход на
 * незаблокированную ферму игрока перезапускает остаток, поэтому пути через
 * цепочки ферм находятся. Возвращает [фермa, ..., цель] либо null.
 */
export const findSupplyPathFromFarm = (farmHexKey, targetHexKey, ctx) => {
  if (farmHexKey === targetHexKey) return null;

  const startBudget = MAX_SUPPLY_DISTANCE;
  const bestBudgetByHex = new Map([[farmHexKey, startBudget]]);
  const parents = new Map();
  let queue = [{hexKey: farmHexKey, budget: startBudget, stateKey: `${farmHexKey}@${startBudget}`}];

  while (queue.length > 0) {
    const nextQueue = [];
    for (const current of queue) {
      if (current.budget <= 0) continue;
      for (const neighborKey of neighborsOf(current.hexKey)) {
        if (!ctx.fieldHexes[neighborKey]) continue;

        // Финиш: конечной ячейке транзитные ограничения (непустота, смежность
        // врагу) не ставятся, но горы/вода и вражеский отряд отсекают и ее —
        // как в каноническом calculateAvailableSupplyCells
        if (neighborKey === targetHexKey) {
          if (SUPPLY_UNPASSABLE_TOKENS.includes(ctx.hexes[neighborKey])) return null;
          if (ctx.enemyUnitHexKeys.has(neighborKey)) return null;
          const endStateKey = `${neighborKey}@end`;
          parents.set(endStateKey, current.stateKey);
          return restorePath(parents, endStateKey);
        }

        if (!isTransitable(neighborKey, ctx)) continue;

        // Ферма игрока перезапускает дальность цепочки
        const nextBudget = isWorkingPlayerFarm(neighborKey, ctx)
          ? MAX_SUPPLY_DISTANCE
          : current.budget - 1;

        const knownBudget = bestBudgetByHex.get(neighborKey);
        if (knownBudget !== undefined && knownBudget >= nextBudget) continue;
        bestBudgetByHex.set(neighborKey, nextBudget);

        const nextStateKey = `${neighborKey}@${nextBudget}`;
        parents.set(nextStateKey, current.stateKey);
        nextQueue.push({hexKey: neighborKey, budget: nextBudget, stateKey: nextStateKey});
      }
    }
    queue = nextQueue;
  }

  return null;
};

/**
 * Промежуточные фермы пути [ферма, ..., цель], которые ПРОДЛЕВАЮТ цепочку
 * снабжения, — рабочие фермы самого игрока (на них перезапускается дальность).
 * Чужая или заблокированная ферма на пути — обычная транзитная ячейка: звеном
 * цепочки она не служит и отметки на анимации пути не получает. Так бывает,
 * например, с фермой крысолюдов, на которой стоит их новобранец за снабжение
 *
 * @param {string[]} path
 * @param {Object} ctx - контекст buildSupplyPathContext
 * @returns {string[]}
 */
export const findSupplyPathRelayFarms = (path, ctx) => path
  .slice(1, -1)
  .filter((hexKey) => isWorkingPlayerFarm(hexKey, ctx));

// BFS от ячейки отряда до ближайшей рабочей фермы игрока (без ограничения
// дальности) по СТРОГИМ правилам. Возвращает {path: [ферма, ..., отряд],
// distance} либо null
const searchNearestFarm = (targetHexKey, ctx) => {
  const visited = new Set([targetHexKey]);
  const parents = new Map();
  let queue = [targetHexKey];
  let distance = 0;

  while (queue.length > 0) {
    const nextQueue = [];
    distance += 1;
    for (const currentKey of queue) {
      for (const neighborKey of neighborsOf(currentKey)) {
        if (visited.has(neighborKey)) continue;
        if (!ctx.fieldHexes[neighborKey]) continue;
        visited.add(neighborKey);

        if (isWorkingPlayerFarm(neighborKey, ctx)) {
          // Ближайшая ферма найдена: разворачиваем путь (шли от отряда)
          parents.set(neighborKey, currentKey);
          const reversed = [];
          let cursor = neighborKey;
          while (cursor) {
            reversed.push(cursor);
            cursor = parents.get(cursor);
          }
          return {path: reversed, distance};
        }

        if (!isTransitable(neighborKey, ctx)) continue;

        parents.set(neighborKey, currentKey);
        nextQueue.push(neighborKey);
      }
    }
    queue = nextQueue;
  }

  return null;
};

// Штраф ослабленного шага (пустая ячейка, вражеский отряд или смежность ему):
// путь визуализации предпочитает реальную застройку — сворачивает на
// «непроводимые» ячейки, только если объезд по застройке заметно длиннее
const RELAXED_STEP_WEIGHT = 4;

// Ослабленный поиск ближайшей фермы для ВИЗУАЛИЗАЦИИ, когда строгих путей
// нет вовсе: пустые ячейки, вражеские отряды и смежные им ячейки проходимы
// (со штрафом), горы/вода — нет. Взвешенная Дейкстра на bucket-очереди.
// Возвращает {path: [ферма, ..., отряд], distance} (distance — в ячейках)
// либо null
const searchNearestFarmRelaxed = (targetHexKey, ctx, checkIfFinish = isWorkingPlayerFarm) => {
  const bestCost = new Map([[targetHexKey, 0]]);
  const parents = new Map();
  const buckets = [[targetHexKey]];

  for (let cost = 0; cost < buckets.length; cost++) {
    const bucket = buckets[cost];
    if (!bucket) continue;
    for (const currentKey of bucket) {
      if (bestCost.get(currentKey) !== cost) continue; // устаревшая запись

      // Финиш корректен при ИЗВЛЕЧЕНИИ вершины с минимальной стоимостью
      if (checkIfFinish(currentKey, ctx)) {
        const reversed = [];
        let cursor = currentKey;
        while (cursor) {
          reversed.push(cursor);
          cursor = parents.get(cursor);
        }
        return {path: reversed, distance: reversed.length - 1};
      }

      for (const neighborKey of neighborsOf(currentKey)) {
        if (!ctx.fieldHexes[neighborKey]) continue;
        const token = ctx.hexes[neighborKey];
        // Горы/вода непроходимы даже для визуализации
        if (SUPPLY_UNPASSABLE_TOKENS.includes(token)) continue;

        const stepWeight = (isTransitable(neighborKey, ctx) || isWorkingPlayerFarm(neighborKey, ctx))
          ? 1
          : RELAXED_STEP_WEIGHT;
        const nextCost = cost + stepWeight;
        const knownCost = bestCost.get(neighborKey);
        if (knownCost !== undefined && knownCost <= nextCost) continue;

        bestCost.set(neighborKey, nextCost);
        parents.set(neighborKey, currentKey);
        buckets[nextCost] = buckets[nextCost] || [];
        buckets[nextCost].push(neighborKey);
      }
    }
  }

  return null;
};

// Сколько первых ячеек пути [ферма, ...] ферма реально покрыла бы снабжением
// по СТРОГИМ правилам: бюджет дальности (с перезапуском на рабочих фермах),
// пустота и вражеские блокировки. Нетранзитная ячейка (пустая или смежная
// врагу) может быть КОНЕЧНОЙ точкой снабжения — отряд, стоящий на ней,
// снабдить можно, путь лишь не продолжается дальше. Поэтому она включается
// в валидную часть (если позволяет дальность) и обрывает разметку; горы/вода
// и вражеский отряд конечной точкой быть не могут
const calcStrictlyValidCellsCount = (path, ctx) => {
  let budget = MAX_SUPPLY_DISTANCE;
  let validCellsCount = 1; // ячейка самой фермы
  for (let i = 1; i < path.length; i++) {
    if (budget <= 0) break;
    const hexKey = path[i];

    if (isTransitable(hexKey, ctx)) {
      budget = isWorkingPlayerFarm(hexKey, ctx) ? MAX_SUPPLY_DISTANCE : budget - 1;
      validCellsCount += 1;
      continue;
    }

    const token = ctx.hexes[hexKey];
    const canBeSupplyEndpoint = !SUPPLY_UNPASSABLE_TOKENS.includes(token)
      && !ctx.enemyUnitHexKeys.has(hexKey);
    if (canBeSupplyEndpoint) {
      validCellsCount += 1;
    }
    break;
  }
  return validCellsCount;
};

/**
 * Перекрытый путь от КОНКРЕТНОЙ фермы к отряду, до которого она не дотягивается: игрок пробует
 * снабдить его с этой фермы, и видно, где снабжение обрывается. Ищется ослабленно, как подсказка
 * этапа перемещения; валидная часть не доходит до отряда, иначе путь выглядел бы проложенным
 *
 * @returns {null | {path: string[], validCellsCount: number}} null — пути нет даже так (горы)
 */
export const findBlockedSupplyPathFromFarm = (farmHexKey, targetHexKey, ctx) => {
  const result = searchNearestFarmRelaxed(targetHexKey, ctx, (hexKey) => hexKey === farmHexKey);
  if (!result || result.path.length < 2) {
    return null;
  }
  return {
    path: result.path,
    validCellsCount: Math.min(calcStrictlyValidCellsCount(result.path, ctx), result.path.length - 1),
  };
};

/**
 * Обратный поиск от ячейки отряда до БЛИЖАЙШЕЙ рабочей фермы игрока — без
 * ограничения дальности (этап перемещения: подсказка «отряд вне зоны
 * снабжения»). Отряд В зоне ⟺ дистанция ≤ MAX_SUPPLY_DISTANCE: последний
 * сегмент любой цепочки ферм — прямой путь ≤ 5 от какой-то фермы, поэтому
 * проверки по ближайшей ферме достаточно и перезапуски здесь не нужны.
 *
 * Если строгих путей нет вовсе (перекрыты вражескими отрядами и смежными им
 * ячейками ЛИБО между отрядом и застройкой пустые ячейки), для ВИЗУАЛИЗАЦИИ
 * ищется ослабленный путь (searchNearestFarmRelaxed) — отряд без снабжения,
 * и подсказка нужна как раз в этих случаях. validCellsCount размечает, докуда
 * ферма реально дотянулась бы по строгим правилам (валидных ячеек может не
 * быть — тогда только сама ферма).
 *
 * @returns {null | {path: string[], distance: number,
 *   isWithinSupplyRange: boolean, validCellsCount: number}}
 *   path — [ферма, ..., ячейка отряда]
 */
export const findNearestFarmSupplyInfo = (targetHexKey, ctx) => {
  const strictResult = searchNearestFarm(targetHexKey, ctx);
  if (strictResult) {
    return {
      ...strictResult,
      isWithinSupplyRange: strictResult.distance <= MAX_SUPPLY_DISTANCE,
      validCellsCount: calcStrictlyValidCellsCount(strictResult.path, ctx),
    };
  }

  const relaxedResult = searchNearestFarmRelaxed(targetHexKey, ctx);
  if (!relaxedResult) {
    return null;
  }
  return {
    ...relaxedResult,
    // Строгих путей нет — отряд заведомо вне зоны снабжения
    isWithinSupplyRange: false,
    validCellsCount: calcStrictlyValidCellsCount(relaxedResult.path, ctx),
  };
};


/**
 * Рабочие фермы игрока, до которых снабжение дотягивается от ОДНОЙ фермы —
 * ровно MAX_SUPPLY_DISTANCE ячеек без перезапуска дальности: цепочка ферм
 * здесь не нужна, каждое звено цепи рисуется своей связью.
 *
 * Найденная ферма дальше не раскрывается: до ее соседей снабжение идет уже
 * своим звеном, от нее. Нерабочая (заблокированная) ферма узлом не считается,
 * но транзитной ячейкой остается — как и в остальных правилах снабжения.
 *
 * @returns {{toHexKey: string, path: string[]}[]} путь — [ферма, ..., ферма]
 */
const findFarmsReachableFromFarm = (farmHexKey, ctx) => {
  const visited = new Set([farmHexKey]);
  const parents = new Map();
  const links = [];
  let queue = [farmHexKey];

  for (let step = 0; step < MAX_SUPPLY_DISTANCE && queue.length > 0; step += 1) {
    const nextQueue = [];
    for (const currentKey of queue) {
      for (const neighborKey of neighborsOf(currentKey)) {
        if (visited.has(neighborKey)) continue;
        if (!ctx.fieldHexes[neighborKey]) continue;
        visited.add(neighborKey);
        parents.set(neighborKey, currentKey);

        // Проверка «ферма» строго ДО транзитной: ферма, смежная вражескому
        // отряду, связью быть может, а дорогой дальше — нет
        if (isWorkingPlayerFarm(neighborKey, ctx)) {
          const path = [];
          let cursor = neighborKey;
          while (cursor) {
            path.push(cursor);
            cursor = parents.get(cursor);
          }
          links.push({toHexKey: neighborKey, path: path.reverse()});
          continue;
        }

        if (!isTransitable(neighborKey, ctx)) continue;
        nextQueue.push(neighborKey);
      }
    }
    queue = nextQueue;
  }

  return links;
};

// Связь между двумя фермами ненаправленная: путь туда и обратно — одна и та
// же линия, рисовать ее дважды незачем
const farmLinkKey = (fromHexKey, toHexKey) => [fromHexKey, toHexKey].sort().join('->');

/**
 * Сеть связей снабжения между фермами игрока: от корневой фермы КО ВСЕМ,
 * до которых от нее дотягивается маршрут снабжения, и дальше так же от каждой
 * найденной фермы — ко всем, до кого дотягивается она. Показывается при
 * постройке фермы, при постановке ее в план строительства и при осмотре ее
 * ячейки (см. scenes/playFarmSupplyNetworkAnimation).
 *
 * Каждая ферма раскрывается один раз, а связи не повторяются: без этого обход
 * зациклился бы на первой же паре ферм, достающих друг до друга, а линии
 * рисовались бы по несколько раз поверх себя.
 *
 * @param {string} rootFarmHexKey
 * @param {Object} ctx - контекст buildSupplyPathContext ОТ ЛИЦА ВЛАДЕЛЬЦА фермы
 * @returns {{links: {fromHexKey: string, toHexKey: string, path: string[]}[], farmHexKeys: string[]}}
 */
export const findFarmSupplyNetwork = (rootFarmHexKey, ctx) => {
  const empty = {links: [], farmHexKeys: []};
  if (!rootFarmHexKey || !isWorkingPlayerFarm(rootFarmHexKey, ctx)) {
    return empty;
  }

  const expandedFarms = new Set();
  const networkFarms = new Set([rootFarmHexKey]);
  const linkKeys = new Set();
  const links = [];
  let queue = [rootFarmHexKey];

  while (queue.length > 0) {
    const nextQueue = [];
    for (const farmHexKey of queue) {
      if (expandedFarms.has(farmHexKey)) continue;
      expandedFarms.add(farmHexKey);

      for (const link of findFarmsReachableFromFarm(farmHexKey, ctx)) {
        const linkKey = farmLinkKey(farmHexKey, link.toHexKey);
        if (!linkKeys.has(linkKey)) {
          linkKeys.add(linkKey);
          links.push({fromHexKey: farmHexKey, toHexKey: link.toHexKey, path: link.path});
        }
        networkFarms.add(link.toHexKey);
        if (!expandedFarms.has(link.toHexKey)) {
          nextQueue.push(link.toHexKey);
        }
      }
    }
    queue = nextQueue;
  }

  return {links, farmHexKeys: [...networkFarms]};
};
